import { useEffect, useId, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Lottie from "lottie-react";
import uploadFileAnimation from "../../assets/upload-file-animation.json";
import { getJobStatus, getProjectById } from "../../api/Projects";
import { Project } from "../../utils/interface/project";
import UserProfileCard from "../../components/UserProfileCard";

const ACCENT = "#E761A0";

/** Circular progress / loading icon for TRACK PROGRESS button */
const TrackProgress = () => {
  const clipId = useId();
  return (
  <svg
    className="w-7 transition-transform duration-500 ease-in-out group-hover:rotate-180"
    id="Group_59"
    data-name="Group 59"
    xmlns="http://www.w3.org/2000/svg"
    width="27.164"
    height="27.154"
    viewBox="0 0 27.164 27.154"
  >
    <defs>
      <clipPath id={clipId}>
        <rect
          id="Rectangle_65"
          data-name="Rectangle 65"
          width="27.164"
          height="27.154"
          fill="#fff"
        />
      </clipPath>
    </defs>
    <g id="Group_58" data-name="Group 58" clipPath={`url(#${clipId})`}>
      <path
        id="Path_201"
        data-name="Path 201"
        d="M475.85,8.9l-5.837,1.9a7.459,7.459,0,0,0-6.423-4.662V0a13.589,13.589,0,0,1,12.26,8.9"
        transform="translate(-449.509)"
        fill="#fff"
      />
      <path
        id="Path_202"
        data-name="Path 202"
        d="M39.39,0V6.137A7.461,7.461,0,0,0,32.966,10.8L27.13,8.9A13.589,13.589,0,0,1,39.39,0"
        transform="translate(-26.306)"
        fill="#fff"
      />
      <path
        id="Path_203"
        data-name="Path 203"
        d="M613.3,328.051a13.558,13.558,0,0,1-5.2,10.689l-3.6-4.959a7.468,7.468,0,0,0,2.452-7.555l5.836-1.9a13.589,13.589,0,0,1,.516,3.721"
        transform="translate(-586.14 -314.479)"
        fill="#fff"
      />
      <path
        id="Path_204"
        data-name="Path 204"
        d="M8.8,333.771,5.2,338.73A13.6,13.6,0,0,1,.516,324.32l5.836,1.9A7.467,7.467,0,0,0,8.8,333.771"
        transform="translate(0 -314.469)"
        fill="#fff"
      />
      <path
        id="Path_205"
        data-name="Path 205"
        d="M212.911,659.749a13.589,13.589,0,0,1-15.152,0l3.6-4.958a7.461,7.461,0,0,0,7.946,0Z"
        transform="translate(-191.753 -634.902)"
        fill="#fff"
      />
    </g>
  </svg>
  );
};

const DEFAULT_PROJECT_NAME = "2 Ruskin Square, London";
const NO_SUMMARY_TEXT =
  "Keep track of your files' progress on the Track Audits page.";

type JobStatusEntry = { status: string | null; output: string | null };

type UploadSuccessLocationState = {
  jobId?: string | null;
  jobIds?: string[] | null;
  status?: string | null;
  output?: string | null;
};

const UploadSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  const navigationState = (location.state as UploadSuccessLocationState | null) ?? null;
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [jobId, setJobId] = useState<string | null>(navigationState?.jobId ?? null);
  const [jobIds, setJobIds] = useState<string[]>(() => navigationState?.jobIds ?? []);
  const [status, setStatus] = useState<string | null>(navigationState?.status ?? null);
  const [jobStatuses, setJobStatuses] = useState<Record<string, JobStatusEntry>>({});

  useEffect(() => {
    setJobId(navigationState?.jobId ?? null);
    setJobIds(navigationState?.jobIds ?? []);
    setStatus(navigationState?.status ?? null);
  }, [navigationState?.jobId, navigationState?.jobIds, navigationState?.output, navigationState?.status]);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    getProjectById(projectId)
      .then((data) => {
        if (!cancelled) {
          setProject(data);
          setError(false);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const idsToPoll = jobIds.length > 0 ? jobIds : jobId ? [jobId] : [];

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (idsToPoll.length === 0) return;

    let cancelled = false;
    let pollingInFlight = false;

    const pollAll = async (): Promise<boolean> => {
      if (cancelled || pollingInFlight) return false;
      pollingInFlight = true;

      try {
        const results = await Promise.all(
          idsToPoll.map((id) => getJobStatus(id).then((data) => ({ id, data })))
        );
        if (cancelled) return false;

        setJobStatuses((prev) => {
          const next = { ...prev };
          results.forEach(({ id, data }) => {
            next[id] = { status: data.status, output: data.output };
          });
          return next;
        });

        if (idsToPoll.length === 1) {
          const single = results[0]?.data;
          if (single) {
            setStatus(single.status);
          }
        }

        const allDone = results.every(
          (r) => r.data.status === "completed" || r.data.status === "failed"
        );
        return allDone;
      } catch {
        return false;
      } finally {
        pollingInFlight = false;
      }
    };

    const interval = window.setInterval(async () => {
      const shouldStop = await pollAll();
      if (shouldStop) window.clearInterval(interval);
    }, 5000);

    void pollAll().then((shouldStop) => {
      if (shouldStop) window.clearInterval(interval);
    });

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [jobId, jobIds.join(",")]);

  const projectName = project?.name ?? DEFAULT_PROJECT_NAME;
  const description = project?.summary?.trim() ? project.summary : NO_SUMMARY_TEXT;

  const handleTrackProgress = () => {
    navigate("/track-audits");
  };

  if (loading) {
    return (
      <div className="min-h-full relative overflow-hidden">
        <div className="relative z-10 pt-0 pb-8 sm:pb-12 sm:px-6 lg:px-0">
          <Link
            to="/project-management"
            className="inline-flex items-center gap-2 font-semibold text-lg hover:underline mb-6 sm:mb-8 lg:mb-10"
            style={{ color: ACCENT }}
          >
            <ArrowLeft className="w-5 h-5 shrink-0" strokeWidth={2} />
            Back to Projects
          </Link>
          <div className="flex justify-center py-16">
            <div
              className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: ACCENT }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full relative overflow-hidden">
      {/* Bottom wavy glowing dots background */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, ${ACCENT} 1.5px, transparent 0)`,
          backgroundSize: "28px 28px",
          backgroundPosition: "0 0",
          maskImage:
            "radial-gradient(ellipse 100% 60% at 50% 100%, black, transparent 60%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 100% 60% at 50% 100%, black, transparent 60%)",
          opacity: 0.5,
        }}
      />
      <div className="absolute bottom-0 left-0 right-0 h-48  pointer-events-none" />

      <div className="relative z-10   sm:px-6 lg:px-0  ">
        <UserProfileCard />
        <Link
          to="/project-management"
          className="inline-flex items-center gap-2 font-semibold text-lg hover:underline mb-6 sm:mb-8 lg:mb-[60px]"
          style={{ color: ACCENT }}
        >
          <ArrowLeft className="w-5 h-5 shrink-0" strokeWidth={2} />
          Back to Projects
        </Link>

        <div className="rounded-[18px] border border-[#868686] overflow-hidden p-6 sm:p-8 md:p-10 lg:p-[29px_47px_29px_0px] max-w-full min-h-[408px]">
          <div className="flex flex-col lg:flex-row  items-center">
            <div className="lg:max-w-max max-w-[300px] ">
              <Lottie
                animationData={uploadFileAnimation}
                loop
              // style={{ width: "100%", height: "100%" }}
              />
            </div>
            <div className="flex-1 min-w-0 ">
              <h1
                className="text-2xl sm:text-3xl lg:text-[44px] font-semibold mb-3 lg:mb-5 xl:mb-[32px]"
                style={{ color: ACCENT }}
              >
                Upload queued successfully
              </h1>
              <p className="text-white text-base sm:text-[22px] font-semibold mb-[17px] xl:mb-[30px]">
                Thank you for uploading your IFC model for{" "}
                <span className="text-[#E761A0]">{projectName}</span>.
              </p>
              {error && (
                <p className="text-amber-400/90 text-sm mb-4 max-w-[566px]">
                  Project details could not be loaded. Showing default message.
                </p>
              )}
              <p className="text-white/80 text-sm sm:text-lg font-light leading-relaxed mb-6 max-w-[566px]">
                {description}
              </p>
              <div className="mb-6 max-w-[566px] rounded-[18px] border border-[#868686] bg-white/5 p-5">
                {idsToPoll.length > 0 ? (
                  <>
                    {idsToPoll.length === 1 ? (
                      <>
                        <p className="text-white/60 text-sm mb-3">Job ID: {idsToPoll[0]}</p>
                        {status === "queued" && (
                          <p className="text-white text-base sm:text-lg">Job queued...</p>
                        )}
                        {status === "processing" && (
                          <p className="text-white text-base sm:text-lg">Processing IFC model...</p>
                        )}
                        {status === "failed" && (
                          <p className="text-red-300 text-base sm:text-lg">Processing failed</p>
                        )}
                        {!status && (
                          <p className="text-white text-base sm:text-lg">Checking job status...</p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-white/60 text-sm mb-3">
                          {idsToPoll.length} job(s) queued. Polling status…
                        </p>
                        {(() => {
                          const entries = idsToPoll.map((id) => ({
                            id,
                            ...jobStatuses[id],
                          }));
                          const completed = entries.filter((e) => e.status === "completed").length;
                          const failed = entries.filter((e) => e.status === "failed").length;
                          const processing = entries.filter((e) => e.status === "processing").length;
                          const queued = entries.filter((e) => e.status === "queued" || !e.status).length;
                          return (
                            <div className="flex flex-col gap-2">
                              <p className="text-white text-base sm:text-lg">
                                {completed} completed, {failed} failed, {processing} processing, {queued} queued
                              </p>
                              <ul className="list-disc list-inside text-white/80 text-sm space-y-1">
                                {entries.map((e, i) => (
                                  <li key={e.id}>
                                    Job {i + 1}: {e.status ?? "checking…"}
                                    {/* {e.output && (
                                      <a
                                        href={e.output}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ml-2 text-[#E761A0] hover:underline"
                                      >
                                        Download
                                      </a>
                                    )} */}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </>
                ) : (
                  <p className="text-white/70 text-sm sm:text-base">
                    No job IDs were provided for this upload, so live processing status is unavailable on this screen.
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={handleTrackProgress}
                className="theme-btn min-w-[237px] group w-full sm:w-auto inline-flex items-center justify-center gap-2 
             px-5 py-3 sm:py-2.5 rounded-[27px] 
               text-white font-semibold text-sm uppercase tracking-wide 
              transition-colors min-h-[44px] rotate-animation"
              >
                <TrackProgress />
                TRACK PROGRESS
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadSuccess;
